import dayjs from 'dayjs';
import { Button, Checkbox, FileInput, Label, Spinner, Textarea, TextInput, Select as FlowbiteSelect } from 'flowbite-react';
import React, { FormEvent, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { sendAssignmentToJobseeker } from 'src/apis/application';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import {
	AssignmentFileOption,
	AssignmentLinkOption,
	AssignmentSubmissionType,
	SendAssignmentToUserInput,
} from 'types';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { useUserContext } from 'src/context/UserProvider';
import { Dropdown, getJobSeekerAssignmentExpiry } from 'shared-utils';
import Select, { SingleValue } from 'react-select';
import { useGetAllPrepCoaches } from 'src/hooks/user.hooks';

const RecruiterCandidateSendAssignment = ({
	userId,
	onSubmit,
	onFinish,
	userName,
	isLoading: outSideApiLoading = false,
	positionId,
}: {
	userId?: string;
	userName?: string;
	positionId?: string;
	isLoading?: boolean;
	onSubmit?: (data: SendAssignmentToUserInput) => void;
	onFinish?: () => void;
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { isAccountManagerLoggedIn } = useUserContext();

	const [file, setFile] = React.useState<File | null>();
	const [isLoading, setIsLoading] = React.useState(outSideApiLoading);
	const [assignmentSubmissionUrl, setAssignmentSubmissionUrl] = React.useState<string>(''); // Separate field for "Other" type submission URL
	const [assginmentFormData, setAssginmentFormData] = React.useState<SendAssignmentToUserInput>({
		positionId: '',
		userId: '',
		prepCoach: '',
		assignmentSubmissionType: AssignmentSubmissionType.Link,
		assignmentLinkOptions: [],
		assignmentFileOptions: [],
	});

	const submissionTypeOptions = useMemo(
		() => [
			{ label: 'Link', value: AssignmentSubmissionType.Link },
			{ label: 'File', value: AssignmentSubmissionType.File },
			{ label: 'Link & File', value: AssignmentSubmissionType.Both },
			{ label: 'Other', value: AssignmentSubmissionType.Other },
		],
		[],
	);

	const linkOptionList: { label: string; value: AssignmentLinkOption }[] = useMemo(
		() => [
			{ label: 'GitHub link', value: 'github' },
			{ label: 'Deployment link', value: 'deployment' },
			{ label: 'Drive link', value: 'drive' },
			{ label: 'Google Collab link', value: 'googleCollab' },
			{ label: 'Submission link', value: 'submission' },
			{ label: 'No Submission', value: 'noSubmission' },
		],
		[],
	);
	const fileOptionList: { label: string; value: AssignmentFileOption }[] = useMemo(
		() => [
			{ label: 'PDF', value: 'pdf' },
			{ label: 'DOC', value: 'doc' },
			{ label: 'DOCX', value: 'docx' },
			{ label: 'ZIP', value: 'zip' },
			{ label: 'Excel', value: 'excel' },
		],
		[],
	);

	const allowsLink =
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Link ||
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Both ||
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Other;
	const allowsFile =
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.File ||
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Both ||
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Other;

	const requiresLinkOptions =
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Link ||
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Both;
	const requiresFileOptions =
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.File ||
		assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Both;
	const linkOptionsSelected = (assginmentFormData.assignmentLinkOptions || []).length > 0;
	const fileOptionsSelected = (assginmentFormData.assignmentFileOptions || []).length > 0;
	const hasAssignmentContent =
		!!assginmentFormData.assignmentDescription || !!assginmentFormData.assignmentLink || !!file;

	const submissionNotesRequired = assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Other;
	const isSubmissionConfigValid =
		(!requiresLinkOptions || linkOptionsSelected) &&
		(!requiresFileOptions || fileOptionsSelected) &&
		(!submissionNotesRequired || !!assginmentFormData.assignmentSubmissionNotes);

	const sendAssignmentToJobseekerFn = useMutation(['sendAssignmentToJobseeker'], sendAssignmentToJobseeker, {
		onSuccess(data) {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({
				title: `Assignment sent to ${userName}`,
				type: 'success',
			});
			setAssginmentFormData({
				positionId: '',
				userId: '',
				prepCoach: '',
			});
			setFile(null);
			setAssignmentSubmissionUrl(''); // Reset submission URL
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const { data: prepCoachesData, isLoading: prepCoachesLoading } = useGetAllPrepCoaches();

	// Validate URL format
	const isValidUrl = (url: string): boolean => {
		if (!url || !url.trim()) {
			return false;
		}
		const trimmed = url.trim();
		try {
			// Try to create a URL object - this validates the format
			const urlObj = new URL(trimmed);
			// Check if it has a valid protocol (http or https)
			if (!['http:', 'https:'].includes(urlObj.protocol)) {
				return false;
			}
			// Check if it has a valid hostname (domain)
			if (!urlObj.hostname || urlObj.hostname.length < 3) {
				return false;
			}
			// Check if hostname contains at least one dot (for domain like example.com)
			// This prevents URLs like "https://test" or "https://invalid"
			if (!urlObj.hostname.includes('.')) {
				return false;
			}
			// Check if hostname doesn't contain spaces (which would be URL encoded)
			if (urlObj.hostname.includes(' ')) {
				return false;
			}
			return true;
		} catch (error) {
			// If URL constructor throws, try adding https:// prefix
			try {
				const urlWithProtocol = trimmed.startsWith('http://') || trimmed.startsWith('https://')
					? trimmed
					: `https://${trimmed}`;
				const urlObj = new URL(urlWithProtocol);
				if (!['http:', 'https:'].includes(urlObj.protocol)) {
					return false;
				}
				if (!urlObj.hostname || urlObj.hostname.length < 3 || !urlObj.hostname.includes('.')) {
					return false;
				}
				if (urlObj.hostname.includes(' ')) {
					return false;
				}
				return true;
			} catch (e) {
				return false;
			}
		}
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (
			!assginmentFormData.assignmentDescription &&
			!assginmentFormData.assignmentLink &&
			!file
		) {
			toast({
				type: 'error',
				title: 'Add a description, link, or document for the assignment',
			});
			return;
		}

		// Validate assignment link if provided
		if (assginmentFormData.assignmentLink && assginmentFormData.assignmentLink.trim()) {
			if (!isValidUrl(assginmentFormData.assignmentLink)) {
				toast({
					type: 'error',
					title: 'Please enter a valid assignment link (e.g., https://example.com/assignment)',
				});
				return;
			}
		}

		// Validate submission URL for "Other" type
		if (assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Other && allowsLink && assignmentSubmissionUrl?.trim()) {
			if (!isValidUrl(assignmentSubmissionUrl)) {
				toast({
					type: 'error',
					title: 'Please enter a valid submission URL (e.g., https://forms.google.com/...)',
				});
				return;
			}
		}

		if (allowsLink && !(assginmentFormData.assignmentLinkOptions || [])?.length && assginmentFormData.assignmentSubmissionType !== AssignmentSubmissionType.Other) {
			toast({
				type: 'error',
				title: 'Select at least one allowed link submission type',
			});
			return;
		}

		if (allowsFile && !(assginmentFormData.assignmentFileOptions || [])?.length && assginmentFormData.assignmentSubmissionType !== AssignmentSubmissionType.Other) {
			toast({
				type: 'error',
				title: 'Select at least one allowed file submission type',
			});
			return;
		}

		if (assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Other) {
			if (!assginmentFormData.assignmentSubmissionNotes) {
				toast({
					type: 'error',
					title: 'Enter submission instructions for the "Other" submission type',
				});
				return;
			}
			if (allowsLink && !assignmentSubmissionUrl?.trim()) {
				toast({
					type: 'error',
					title: 'Enter Submission URL for the "Other" submission type',
				});
				return;
			}
		}

		let imageUrl = assginmentFormData.assignmentFileLink || '';
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			setIsLoading(true);

			const policy = await getPolicyForS3NonVideo(file.name, 'recruiterAssignments', file.type, true);
			const uploadedUrl = await handleS3Upload(policy, file);
			setIsLoading(false);
			if (uploadedUrl) imageUrl = uploadedUrl;
		}

		const payload: SendAssignmentToUserInput = {
			...assginmentFormData,
			assignmentFileLink: imageUrl,
			positionId: positionId ?? '',
			userId: userId ?? '',
			// For "Other" type, use assignmentSubmissionUrl as assignmentLink (submission URL)
			// Keep assignmentLink separate for the assignment link field
			assignmentLink: assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Other && allowsLink
				? assignmentSubmissionUrl
				: assginmentFormData.assignmentLink,
		};

		if (onSubmit) {
			onSubmit(payload);
		} else {
			sendAssignmentToJobseekerFn.mutate(payload);
		}
	};

	useEffect(() => {
		setAssginmentFormData({
			positionId: '',
			userId: '',
			prepCoach: '',
			assignmentSubmissionType: AssignmentSubmissionType.Link,
			assignmentLinkOptions: [],
			assignmentFileOptions: [],
		});
		setAssignmentSubmissionUrl(''); // Reset submission URL
	}, []);

	useEffect(() => {
		setAssginmentFormData(prev => {
			const submissionType = prev.assignmentSubmissionType ?? AssignmentSubmissionType.Link;
			const nextAllowsLink =
				submissionType === AssignmentSubmissionType.Link ||
				submissionType === AssignmentSubmissionType.Both ||
				submissionType === AssignmentSubmissionType.Other;
			const nextAllowsFile =
				submissionType === AssignmentSubmissionType.File ||
				submissionType === AssignmentSubmissionType.Both ||
				submissionType === AssignmentSubmissionType.Other;

			return {
				...prev,
				assignmentLinkOptions: nextAllowsLink ? prev.assignmentLinkOptions : [],
				assignmentFileOptions: nextAllowsFile ? prev.assignmentFileOptions : [],
				assignmentSubmissionNotes:
					submissionType === AssignmentSubmissionType.Other ? prev.assignmentSubmissionNotes : undefined,
			};
		});
	}, [assginmentFormData.assignmentSubmissionType]);

	const prepCoachesOptions =
		prepCoachesData?.users?.map(u => ({ label: `${u.name} <${u.email}>`, value: String(u._id) })) ?? [];

	return (
		<form onSubmit={handleSubmit}>
			<div className="mt-[16px]">
				<div className="mb-[8px] flex justify-between items-end">
					<div>
						<Label>{'Assignment link '}</Label>
						<span className="text-[red]">*</span>
					</div>
				</div>
				<TextInput
					type="url"
					placeholder="https://example.com/assignment"
					value={assginmentFormData.assignmentLink || ''}
					onChange={e => {
						const value = e.target.value;
						setAssginmentFormData(prev => ({ ...prev, assignmentLink: value }));
					}}
					helperText="Enter a valid URL starting with http:// or https://"
				/>
			</div>

			<div>
				<div className="mb-[8px] flex justify-between items-end">
					<div>
						<Label>{'Assignment description '}</Label>
					</div>

					<span className="italic text-xs text-[#6B7280] ">
						{(assginmentFormData?.assignmentDescription?.length || 0) + ' / ' + 640}
					</span>
				</div>

				<Textarea
					name="message"
					maxLength={640}
					value={assginmentFormData.assignmentDescription}
					placeholder="Write your question here ..."
					className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[185px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
					onChange={e => {
						setAssginmentFormData(prev => {
							return { ...prev, assignmentDescription: e.target.value };
						});
					}}
				></Textarea>
			</div>

			<div className="relative mt-[16px]">
				<div className="mb-[8px]">
					<Label>{'Upload file '}</Label>
				</div>

				<FileInput
					id="hukumu-profile-resume-file-input"
					accept=".pdf,doc,docx"
					disabled={isLoading}
					value={assginmentFormData?.assignmentFileLink}
					helperText="Only txt, pdf, doc files allowed."
					className="w-full text-xs font-normal italic min-h-[42px] [&~p]:italic"
					onChange={event => {
						if (event.target.files) {
							setFile && setFile(event.target.files[0]);
						}
					}}
				/>
			</div>



			<div className="mt-[16px]">
				<div className="mb-[8px] flex items-center gap-1">
					<Label>{'Assignment submission type'}</Label>
					<span className="text-[red]">*</span>
				</div>
				<FlowbiteSelect
					value={assginmentFormData.assignmentSubmissionType}
					onChange={e =>
						setAssginmentFormData(prev => ({
							...prev,
							assignmentSubmissionType: e.target.value as AssignmentSubmissionType,
						}))
					}
				>
					{submissionTypeOptions.map(option => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</FlowbiteSelect>
			</div>

			{allowsLink && assginmentFormData.assignmentSubmissionType !== AssignmentSubmissionType.Other ? (
				<div className="mt-[16px]">
					<Label className="mb-[8px] block">Allowed link formats</Label>
					<div className="grid grid-cols-2 gap-2">
						{linkOptionList.map(option => (
							<label key={option.value} className="flex items-center gap-2 text-sm">
								<Checkbox
									checked={assginmentFormData.assignmentLinkOptions?.includes(option.value)}
									onChange={event => {
										const checked = event.target.checked;
										setAssginmentFormData(prev => {
											const prevOptions = prev.assignmentLinkOptions || [];
											return {
												...prev,
												assignmentLinkOptions: checked
													? [...prevOptions, option.value]
													: prevOptions.filter(val => val !== option.value),
											};
										});
									}}
								/>
								<span>{option.label}</span>
							</label>
						))}
					</div>
				</div>
			) : null}

			{allowsFile && assginmentFormData.assignmentSubmissionType !== AssignmentSubmissionType.Other ? (
				<div className="mt-[16px]">
					<Label className="mb-[8px] block">Allowed file formats</Label>
					<div className="grid grid-cols-2 gap-2">
						{fileOptionList.map(option => (
							<label key={option.value} className="flex items-center gap-2 text-sm">
								<Checkbox
									checked={assginmentFormData.assignmentFileOptions?.includes(option.value)}
									onChange={event => {
										const checked = event.target.checked;
										setAssginmentFormData(prev => {
											const prevOptions = prev.assignmentFileOptions || [];
											return {
												...prev,
												assignmentFileOptions: checked
													? [...prevOptions, option.value]
													: prevOptions.filter(val => val !== option.value),
											};
										});
									}}
								/>
								<span>{option.label}</span>
							</label>
						))}
					</div>
				</div>
			) : null}

			{assginmentFormData.assignmentSubmissionType === AssignmentSubmissionType.Other ? (
				<>
					{allowsLink && (
						<div className="mt-[16px]">
							<div className="mb-[8px] flex items-center gap-1">
								<Label>{'Submission URL'}</Label>
								<span className="text-[red]">*</span>
							</div>
							<TextInput
								type="url"
								placeholder="https://forms.google.com/..."
								value={assignmentSubmissionUrl}
								helperText="Enter a valid URL starting with http:// or https://"
								onChange={e => {
									setAssignmentSubmissionUrl(e.target.value);
								}}
							/>
							<p className="mt-1 text-xs text-gray-500">
								Enter the URL where students should submit their assignment (e.g., Google Form, Typeform, etc.)
							</p>
						</div>
					)}
					<div className="mt-[16px]">
						<div className="mb-[8px] flex items-center gap-1">
							<Label>{'Submission instructions'}</Label>
							<span className="text-[red]">*</span>
						</div>
						<Textarea
							value={assginmentFormData.assignmentSubmissionNotes || ''}
							placeholder="Describe how the jobseeker should submit the assignment"
							onChange={e =>
								setAssginmentFormData(prev => ({ ...prev, assignmentSubmissionNotes: e.target.value }))
							}
						/>
					</div>
				</>
			) : null}

			{/* Prep Coach */}
			{isAccountManagerLoggedIn ? (
				<div className="mt-[16px] whiteBgSelect">
					<div className="mb-[8px]">
						<Label>{'Prep Coach'}</Label>
						<span className="text-[red]">*</span>{' '}
					</div>
					<Select
						classNamePrefix="my-select-plans"
						className="single-value-select-tag"
						isLoading={prepCoachesLoading}
						options={prepCoachesOptions}
						closeMenuOnSelect
						value={prepCoachesOptions.find(op => op.value === assginmentFormData.prepCoach)}
						onChange={(val: SingleValue<Dropdown>) => {
							if (val) setAssginmentFormData(prev => ({ ...prev, prepCoach: String(val.value) }));
						}}
					/>
				</div>
			) : null}

			<div className="mt-[16px]">
				<div className="mb-[8px]">
					<Label>{'Submission deadline '}</Label>
					<span className="text-[gray]">(Optional)</span>
				</div>

				<MobileDateTimePicker
					value={assginmentFormData.assignmentDeadline ? dayjs(assginmentFormData.assignmentDeadline) : null}
					minDateTime={dayjs()}
					format="DD MMM, YYYY, hh:mm A"
					onChange={date => {
						const dateValue = date as { $d?: Date };
						setAssginmentFormData(prev => {
							return {
								...prev,
								assignmentDeadline: dateValue?.$d || undefined,
								jobSeekerAssignmentDeadline: dateValue?.$d ? dayjs(getJobSeekerAssignmentExpiry(dateValue.$d)).toDate() : undefined,
							};
						});
					}}
					sx={{
						'&': {
							width: '100%',
						},
						'& .MuiInputBase-input': {
							padding: '10px',
							borderRadius: '8px',
							backgroundColor: 'white',
							border: '1px solid rgb(209 213 219 / 1)',
							fontSize: '14px',
						},
						'& .MuiInputBase-root': {
							outline: 'none',
							padding: '0px',
							'&:hover': {
								outline: 'none',
							},
						},
						'& fieldset': {
							display: 'none',
						},
					}}
				/>
			</div>

			{isAccountManagerLoggedIn ? (
				<div className="mt-[16px]">
					<div className="mb-[8px]">
						<Label>{'Jobseeker Submission deadline '}</Label>
						<span className="text-[gray]">(Optional)</span>
					</div>

					<MobileDateTimePicker
						value={assginmentFormData.jobSeekerAssignmentDeadline ? dayjs(assginmentFormData.jobSeekerAssignmentDeadline) : null}
						minDateTime={dayjs()}
						format="DD MMM, YYYY, hh:mm A"
						onChange={date => {
							const dateValue = date as { $d?: Date };
							setAssginmentFormData(prev => {
								return { ...prev, jobSeekerAssignmentDeadline: dateValue?.$d || undefined };
							});
						}}
						sx={{
							'&': {
								width: '100%',
							},
							'& .MuiInputBase-input': {
								padding: '10px',
								borderRadius: '8px',
								backgroundColor: 'white',
								border: '1px solid rgb(209 213 219 / 1)',
								fontSize: '14px',
							},
							'& .MuiInputBase-root': {
								outline: 'none',
								padding: '0px',
								'&:hover': {
									outline: 'none',
								},
							},
							'& fieldset': {
								display: 'none',
							},
						}}
					/>
				</div>
			) : null}

			<Button
				type="submit"
				className="w-full min-h-[42px] mt-[20px] bg-[#7777E7]"
				color="purplePlaced"
				disabled={
					sendAssignmentToJobseekerFn.isLoading ||
					outSideApiLoading ||
					!hasAssignmentContent ||
					(isAccountManagerLoggedIn && !assginmentFormData.prepCoach) ||
					!isSubmissionConfigValid
				}
			>
				{sendAssignmentToJobseekerFn.isLoading || outSideApiLoading ? (
					<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
						<Spinner />
					</div>
				) : (
					'Send Assignment'
				)}
			</Button>
		</form>
	);
};

export default RecruiterCandidateSendAssignment;
